import { useState } from "react";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { z } from "zod";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { useActiveOrganisation } from "@/app/Organisations/Hooks/useActiveOrganisation";
import { useTranslations } from "use-intl";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { parse } from "graphql";

type CreateInvitationData = {
  createInvitation: { id: string; email: string };
};
type CreateInvitationVars = {
  organisation_id: string;
  email: string;
};

const createInvitationMutation = parse(`
  mutation CreateInvitation(
    $organisation_id: ID!
    $email: String!
  ) {
    createInvitation(
      organisation_id: $organisation_id
      email: $email
    ) {
      id
      email
    }
  }
`) as unknown as TypedDocumentNode<CreateInvitationData, CreateInvitationVars>;

type InviteMemberFormValues = {
  email_address: string;
};

export function InviteOrganizationMemberDialog({
  organisation_id,
}: {
  organisation_id?: string;
}) {
  const [, setError] = useState<string | null>(null);
  const { activeOrganisationId } = useActiveOrganisation();
  const resolvedOrgId = organisation_id ?? activeOrganisationId;
  const t = useTranslations("settings.team.invite");

  const inviteMemberSchema = z.object({
    email_address: z.email(t("validEmailError")),
  });

  const { mutateAsync } = useGqlMutation(createInvitationMutation);
  const { close } = useDialogState();

  async function handleSubmit(values: InviteMemberFormValues) {
    setError(null);

    try {
      await mutateAsync({
        organisation_id: resolvedOrgId!,
        email: values.email_address,
      });
      return close();
    } catch (err: any) {
      const errorMessage =
        err?.response?.errors?.[0]?.message ||
        err?.message ||
        t("errorMessage");
      setError(errorMessage);
      throw err;
    }
  }

  return (
    <BaseDialog
      title={t("dialogTitle")}
      subtitle={t("dialogSubtitle")}
      size="md"
    >
      <GenericForm<InviteMemberFormValues>
        initialValues={{
          email_address: "",
        }}
        onSubmit={handleSubmit}
        validationSchema={toFormikValidationSchema(inviteMemberSchema)}
      >
        <TextInput
          name="email_address"
          label={t("emailLabel")}
          type="email"
          inputProps={{ placeholder: t("emailPlaceholder") }}
          className="col-span-full"
          autoFocus
        />
        <DialogSubmitButton text={t("sendInvitation")} />
      </GenericForm>
    </BaseDialog>
  );
}
